import { FC } from 'react';
import classNames from 'classnames';

import { IconSvg } from '@/components';
import { LabResult } from '@/types';
import {
  CBD_COMPOUNDS,
  TERPENES,
  THC_COMPOUNDS,
  TOTAL_CANNABINOIDS,
} from '@/constants';
import { getSortedCannabinoidsCompounds } from '@/utils';

interface ILabResultsProps {
  labResults: LabResult[];
}

const LabResults: FC<ILabResultsProps> = ({ labResults }) => {
  if (!labResults || labResults.length === 0) return null;

  const allTerpenesAvailables = TERPENES.map(e => e.labId);
  const terpenes = labResults
    .filter(res => allTerpenesAvailables.includes(res.result_type))
    .sort(
      (a, b) =>
        parseFloat(b.amount.maximum_value) - parseFloat(a.amount.maximum_value),
    );

  const cannabinoids = labResults.filter(
    res => !allTerpenesAvailables.includes(res.result_type),
  );

  const totalCannabinoids = cannabinoids?.find(
    c => c.result_type === TOTAL_CANNABINOIDS.labId,
  );

  const cbdCompounds = CBD_COMPOUNDS.map(comp => {
    const cannabinoid = cannabinoids.find(c => c.result_type === comp);

    if (cannabinoid) {
      return cannabinoid;
    }
  }).filter(element => element !== undefined);

  const thcCompounds = THC_COMPOUNDS.map(comp => {
    const cannabinoid = cannabinoids.find(c => c.result_type === comp);

    if (cannabinoid) {
      return cannabinoid;
    }
  }).filter(element => element !== undefined);

  const CannabinoidCompound = ({ compound, title }) => {
    if (!compound || compound.length === 0) return null;

    if (title === TOTAL_CANNABINOIDS.short) {
      return (
        <div className="cannabinoid_compound_section">
          <p className="list_title">{title}</p>
          <div className="cannabinoid_list">
            <div className="cannabinoid_item" key={compound.result_type}>
              <p className="cannabinoid_name">{TOTAL_CANNABINOIDS.name}</p>
              <p className="cannabinoid_value">
                {compound.amount.maximum_value}
                {compound.amount_type === 'PERCENTAGE' && '%'}
                {compound.amount_type === 'MG' && 'mg'}
              </p>
            </div>
          </div>
        </div>
      );
    }

    const sortedCompounds = getSortedCannabinoidsCompounds(compound);

    return (
      <div className="cannabinoid_compound_section">
        <p className="list_title">{title}</p>
        <div className="cannabinoid_list">
          {sortedCompounds?.map(c => (
            <div className="cannabinoid_item" key={c.result_type}>
              <p className="cannabinoid_name">{c.result_type}</p>
              <p className="cannabinoid_value">
                {c.amount.maximum_value}
                {c.amount_type === 'PERCENTAGE' && '%'}
                {c.amount_type === 'MG' && 'mg'}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const existsCbdCompounds = cbdCompounds && cbdCompounds.length > 0;
  const existsThcCompounds = thcCompounds && thcCompounds.length > 0;
  const existsTerpenes = terpenes && terpenes.length > 0;

  const oneElement =
    (!existsCbdCompounds && !existsThcCompounds) || !existsTerpenes;

  const CannabinoidsSection = () => {
    const cannabinoidsQty = [
      existsCbdCompounds,
      existsThcCompounds,
      totalCannabinoids,
    ].filter(Boolean)?.length;

    if (cannabinoidsQty === 0) {
      return null;
    }

    return (
      <div className="cannabinoids">
        <p className="details__h3">Cannabinoids</p>

        <div
          className={classNames('cannabinoids__container', {
            ['single_element']: cannabinoidsQty === 1,
          })}
        >
          {existsThcCompounds && (
            <CannabinoidCompound compound={thcCompounds} title="THC" />
          )}
          {existsCbdCompounds && (
            <CannabinoidCompound compound={cbdCompounds} title="CBD" />
          )}
          <CannabinoidCompound
            compound={totalCannabinoids}
            title={TOTAL_CANNABINOIDS.short}
          />
        </div>
      </div>
    );
  };

  return (
    <div
      className={classNames('lab_results', {
        ['one_element']: oneElement,
      })}
    >
      {existsTerpenes && (
        <div className="terpenes">
          <p className="details__h3">Terpenes</p>
          <div className={'terpene_list'}>
            {terpenes.map(t => {
              const terpene = TERPENES.find(ter => ter.labId === t.result_type);

              if (!terpene) {
                return null;
              }

              return (
                <div className="terpene_item" key={terpene.labId}>
                  {terpene.icon && (
                    <figure>
                      <IconSvg name={terpene.icon} />
                    </figure>
                  )}
                  <div className="terpene_info">
                    <div className="terpene_header">
                      <span className="terpene_name">{terpene.name}</span>
                      <span className="terpene_percentage">
                        {t.amount.maximum_value}{' '}
                        {t.amount_type === 'PERCENTAGE' && '%'}
                      </span>
                    </div>
                    {terpene?.aroma && terpene?.effects && (
                      <div className="terpene_description">
                        <span className="terpene_aroma">{terpene.aroma}: </span>
                        {terpene.effects}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <CannabinoidsSection />
    </div>
  );
};

export { LabResults };
